import { ALL_US_STATES, ALL_US_STATES_ABBRV } from "@/lib/usStateHelpers";

const SECTION_SUFFIX = /\s+section$/i;

export function normalizeSectionName(name: string | null | undefined): string {
  return (name || "").trim().replace(SECTION_SUFFIX, "").replace(/\s+/g, " ").toLowerCase();
}

/**
 * PGA section display names (with or without a trailing " Section") mapped to
 * the US states they primarily cover. International / National have none.
 */
export const PGA_SECTION_STATES: Record<string, string[]> = {
  "alabama - nw florida": ["AL", "FL"],
  aloha: ["HI"],
  carolinas: ["NC", "SC"],
  "central new york": ["NY"],
  colorado: ["CO"],
  connecticut: ["CT"],
  gateway: ["MO", "IL"],
  georgia: ["GA"],
  "gulf states": ["LA", "MS", "AL"],
  illinois: ["IL"],
  indiana: ["IN"],
  international: [],
  iowa: ["IA"],
  kentucky: ["KY"],
  metropolitan: ["NY", "CT"],
  michigan: ["MI"],
  "middle atlantic": ["VA", "MD", "DC", "WV"],
  midwest: ["KS", "MO"],
  minnesota: ["MN"],
  national: [],
  nebraska: ["NE"],
  "new england": ["MA", "ME", "NH", "VT", "RI"],
  "new jersey": ["NJ"],
  "north florida": ["FL"],
  "northeastern new york": ["NY"],
  "northern california": ["CA"],
  "northern ohio": ["OH"],
  "northern texas": ["TX"],
  "pacific northwest": ["WA", "OR", "ID"],
  philadelphia: ["PA", "NJ", "DE"],
  "rocky mountain": ["MT", "WY", "ID"],
  "south central": ["OK", "AR"],
  "south florida": ["FL"],
  "southern california": ["CA"],
  "southern ohio": ["OH"],
  "southern texas": ["TX"],
  southwest: ["AZ"],
  "sun country": ["NM"],
  tennessee: ["TN"],
  "tri-state": ["PA", "WV", "OH"],
  utah: ["UT"],
  "western new york": ["NY"],
  wisconsin: ["WI"]
};

export const US_STATE_ADJACENCY: Record<string, string[]> = {
  AL: ["FL", "GA", "MS", "TN"],
  AK: [],
  AZ: ["CA", "CO", "NM", "NV", "UT"],
  AR: ["LA", "MO", "MS", "OK", "TN", "TX"],
  CA: ["AZ", "NV", "OR"],
  CO: ["AZ", "KS", "NE", "NM", "OK", "UT", "WY"],
  CT: ["MA", "NY", "RI"],
  DE: ["MD", "NJ", "PA"],
  DC: ["MD", "VA"],
  FL: ["AL", "GA"],
  GA: ["AL", "FL", "NC", "SC", "TN"],
  HI: [],
  ID: ["MT", "NV", "OR", "UT", "WA", "WY"],
  IL: ["IA", "IN", "KY", "MO", "WI"],
  IN: ["IL", "KY", "MI", "OH"],
  IA: ["IL", "MN", "MO", "NE", "SD", "WI"],
  KS: ["CO", "MO", "NE", "OK"],
  KY: ["IL", "IN", "MO", "OH", "TN", "VA", "WV"],
  LA: ["AR", "MS", "TX"],
  ME: ["NH"],
  MD: ["DC", "DE", "PA", "VA", "WV"],
  MA: ["CT", "NH", "NY", "RI", "VT"],
  MI: ["IN", "OH", "WI"],
  MN: ["IA", "ND", "SD", "WI"],
  MS: ["AL", "AR", "LA", "TN"],
  MO: ["AR", "IA", "IL", "KS", "KY", "NE", "OK", "TN"],
  MT: ["ID", "ND", "SD", "WY"],
  NE: ["CO", "IA", "KS", "MO", "SD", "WY"],
  NV: ["AZ", "CA", "ID", "OR", "UT"],
  NH: ["MA", "ME", "VT"],
  NJ: ["DE", "NY", "PA"],
  NM: ["AZ", "CO", "OK", "TX", "UT"],
  NY: ["CT", "MA", "NJ", "PA", "VT"],
  NC: ["GA", "SC", "TN", "VA"],
  ND: ["MN", "MT", "SD"],
  OH: ["IN", "KY", "MI", "PA", "WV"],
  OK: ["AR", "CO", "KS", "MO", "NM", "TX"],
  OR: ["CA", "ID", "NV", "WA"],
  PA: ["DE", "MD", "NJ", "NY", "OH", "WV"],
  RI: ["CT", "MA"],
  SC: ["GA", "NC"],
  SD: ["IA", "MN", "MT", "ND", "NE", "WY"],
  TN: ["AL", "AR", "GA", "KY", "MO", "MS", "NC", "VA"],
  TX: ["AR", "LA", "NM", "OK"],
  UT: ["AZ", "CO", "ID", "NM", "NV", "WY"],
  VT: ["MA", "NH", "NY"],
  VA: ["DC", "KY", "MD", "NC", "TN", "WV"],
  WA: ["ID", "OR"],
  WV: ["KY", "MD", "OH", "PA", "VA"],
  WI: ["IA", "IL", "MI", "MN"],
  WY: ["CO", "ID", "MT", "NE", "SD", "UT"]
};

export function statesForSectionName(name: string | null | undefined): string[] {
  const key = normalizeSectionName(name);
  if (!key) return [];
  return PGA_SECTION_STATES[key] ?? [];
}

const STATE_ABBR_FOR_NAMES = [
  "AL",
  "AK",
  "AZ",
  "AR",
  "CA",
  "CO",
  "CT",
  "DE",
  "FL",
  "GA",
  "HI",
  "ID",
  "IL",
  "IN",
  "IA",
  "KS",
  "KY",
  "LA",
  "ME",
  "MD",
  "MA",
  "MI",
  "MN",
  "MS",
  "MO",
  "MT",
  "NE",
  "NV",
  "NH",
  "NJ",
  "NM",
  "NY",
  "NC",
  "ND",
  "OH",
  "OK",
  "OR",
  "PA",
  "RI",
  "SC",
  "SD",
  "TN",
  "TX",
  "UT",
  "VT",
  "VA",
  "WA",
  "WV",
  "WI",
  "WY"
] as const;

const STATE_NAME_TO_ABBR: Record<string, string> = {
  ...Object.fromEntries(ALL_US_STATES.map((name, index) => [name.toLowerCase(), STATE_ABBR_FOR_NAMES[index]])),
  "district of columbia": "DC"
};

export function parseStateFromLocation(location: string | null | undefined): string | null {
  if (!location) return null;
  const trimmed = location.trim();
  const abbrMatch = trimmed.match(/,\s*([A-Za-z]{2})\s*$/);
  if (abbrMatch) {
    const abbr = abbrMatch[1].toUpperCase();
    if (ALL_US_STATES_ABBRV.includes(abbr)) return abbr;
  }
  const token = trimmed
    .split(/[,\s]+/)
    .pop()
    ?.toLowerCase();
  if (!token) return null;
  if (token.length === 2 && ALL_US_STATES_ABBRV.includes(token.toUpperCase())) {
    return token.toUpperCase();
  }
  return STATE_NAME_TO_ABBR[token] ?? STATE_NAME_TO_ABBR[trimmed.toLowerCase()] ?? null;
}
